import { useQuery } from "@tanstack/react-query";
import { Wallet } from "lucide-react";
import { fetchAdminOrderWallets } from "@veorest/api-client";
import { formatDate, formatIqd } from "../../lib/format";

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-4 py-1.5">
      <span className="text-xs font-bold uppercase tracking-wide text-[var(--text-muted)]">
        {label}
      </span>
      <span className="text-sm font-semibold text-[var(--text-primary)] text-right break-words">
        {value}
      </span>
    </div>
  );
}

const num = (v: unknown) => Number(v ?? 0);
const str = (v: unknown, fallback = "—") =>
  v === null || v === undefined || v === "" ? fallback : String(v);

/** Order desk — multi-wallet v2 settlement picture (fee-only rail). */
export function OrderWalletSettlementCard({ orderId }: { orderId: string }) {
  const query = useQuery({
    queryKey: ["admin-order-wallets", orderId],
    enabled: Boolean(orderId),
    queryFn: () => fetchAdminOrderWallets(orderId),
  });

  const data = query.data;
  const fee = data?.platform_fee ?? null;
  const settlement = data?.driver_settlement ?? null;
  const payer = data?.payer_wallet ?? null;
  const driverWallet = data?.driver_wallet ?? null;

  return (
    <section className="rounded-xl border border-[var(--border-subtle)] bg-[var(--surface)] p-4">
      <div className="mb-2 flex items-center gap-2 text-[var(--text-muted)]">
        <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg border border-[var(--border-subtle)]">
          <Wallet className="h-4 w-4" />
        </span>
        <h3 className="text-sm font-black uppercase tracking-wide">Wallet settlement (v2)</h3>
      </div>

      {query.isLoading ? (
        <p className="text-sm text-[var(--text-muted)]">Loading wallet settlement…</p>
      ) : query.isError ? (
        <p className="text-sm font-semibold text-red-500">
          Could not load wallet settlement: {(query.error as Error)?.message}
        </p>
      ) : (
        <>
          <Row
            label="Platform fee"
            value={
              fee
                ? `${formatIqd(num(fee.amount))} · ${str(fee.payer_wallet_type)} · ${formatDate(str(fee.created_at, ""))}`
                : "not charged (order completed before Phase 2)"
            }
          />
          <Row
            label="Payer wallet"
            value={
              payer
                ? `${str(payer.owner_name, payer.wallet_type)} · balance ${formatIqd(num(payer.balance))} · limit ${formatIqd(num(payer.negative_credit_limit))}`
                : "—"
            }
          />
          <Row
            label="Driver settlement"
            value={
              settlement
                ? `${str(settlement.fleet_type)} · earnings ${formatIqd(num(settlement.driver_earnings))} · cash ${formatIqd(num(settlement.cash_collected))}${
                    num(settlement.platform_commission) > 0
                      ? ` · commission ${formatIqd(num(settlement.platform_commission))}`
                      : ""
                  }`
                : "not settled"
            }
          />
          <Row
            label="Driver wallet"
            value={
              driverWallet
                ? `${str(driverWallet.wallet_type)} · balance ${formatIqd(num(driverWallet.balance))}`
                : "—"
            }
          />
          <Row
            label="Driver pay snapshot"
            value={
              data?.orders_driver_trip_fee != null
                ? formatIqd(data.orders_driver_trip_fee)
                : "—"
            }
          />
          <p className="mt-2 border-t border-[var(--border-subtle)] pt-2 text-xs font-semibold text-[var(--text-muted)]">
            Fee-only rail: 250 IQD per completed order + driver-cash settlements. Food / order-item
            values are store revenue and never part of wallet math.
          </p>
        </>
      )}
    </section>
  );
}
